// List Post data.
// Author : Aysegul Yalc'nkaya
// @param {string}   cyPrefix      Prefix for data-cy.
// @param {Object}   data          Data object
// @param {string[]} selectedItems Selected items' Id to check if the current list is selected or not.
// @param {string[]} buttons       List of buttons in the components.

<template>
  <b-colxx xxs="12" class="mb-3" v-if="data.status=='success' || data.status=='pending' || data.status=='scheduled' || data.type=='scheduled'">
  <b-card
    :data-cy="`${cyPrefix}`"
    @click="onClickEvent($event, data.id)"
    :class="{'d-flex flex-row':true, 'highlight-latest': highlight}"
    no-body
  >
    <!-- Dialog: Date/Time -->
    <dialog-date-time
      :disabled="data.showOnly"
      :data="data"
      :dialog="dialogs.schedule"
      @dialog-close="closeDialogSchedule"
      @dialog-submit="onSubmitDialogSchedule"
    />
    <!-- Dialog: Alert -->
    <dialog-alert
      :dialog="dialogs.alert"
      :description="$t('alert.delete')"
      :actionType="$t('buttons.delete')"
      :buttonSubmitText="$t('buttons.delete')"
      @dialog-close="closeDialogAlert"
      @dialog-submit="onSubmitDialogAlert"
    />

    <!-- Image
    <div class="align-self-center">
      <router-link :to="`?p=${data.id}`" class="d-flex">
        <b-img
          :src="(Array.isArray(data.mediaUrls) && (data.mediaUrls.length >0)) ? data.mediaUrls[0] : '/assets/img/no-image.png'"
          class="list-thumbnail responsive border-0"
          :alt="data.id"
          />
      </router-link>
    </div>
    -->

    <div class="pl-2 d-flex flex-grow-1 min-width-zero">

      <div class="card-body align-self-center flex-lg-row justify-content-between min-width-zero align-items-lg-center w-60" >

          <b-row>

            <!-- Badge : platform -->
            <BadgeListPlatform
              v-if="platformBadges.length > 0"
              :cyPrefix="`${cyPrefix}`"
              :data="platformBadges"
            />


          </b-row>
           <!-- Description of Product -->
          <b-row>
            <p :data-cy="`${cyPrefix}-description`" class="mb-1 text-muted">{{data.post}}</p>
          </b-row>

          <!-- Combined Date, Status and Actions Row -->
          <b-row v-if="data.scheduleDate || data.status">
            <div class="d-flex align-items-center mb-1 text-small">
              <small v-if="data.scheduleDate" class="text-muted mr-2">
                <i class="simple-icon-calendar"></i>
                {{ formatDate(data.scheduleDate) }}
              </small>
              <span v-if="data.status" class="status-badge ml-2" :class="data.status.toLowerCase()">
                {{ formatStatus(data.status) }}
              </span>
              
            </div>
          </b-row>

        <!-- Engagement Metrics -->
        <b-row v-if="data.analytics && (data.analytics.totalLikes > 0 || data.analytics.totalComments > 0 || data.analytics.totalShares > 0 || data.analytics.totalViews > 0 || data.analytics.totalReach > 0)">
          <div class="engagement-metrics mt-2">
            <div class="d-flex flex-wrap gap-2">
              <span v-if="data.analytics.totalLikes > 0" class="engagement-badge likes">
                <i class="simple-icon-heart"></i>
                {{ formatNumber(data.analytics.totalLikes) }}
              </span>
              <span v-if="data.analytics.totalComments > 0" class="engagement-badge comments">
                <i class="simple-icon-bubble"></i>
                {{ formatNumber(data.analytics.totalComments) }}
              </span>
              <span v-if="data.analytics.totalShares > 0" class="engagement-badge shares">
                <i class="simple-icon-share"></i>
                {{ formatNumber(data.analytics.totalShares) }}
              </span>
              <span v-if="data.analytics.totalViews > 0" class="engagement-badge views">
                <i class="simple-icon-eye"></i>
                {{ formatNumber(data.analytics.totalViews) }}
              </span>
              <span v-if="data.analytics.totalReach > 0" class="engagement-badge reach">
                <i class="simple-icon-people"></i>
                {{ formatNumber(data.analytics.totalReach) }}
              </span>
            </div>
            
            <!-- Platform Breakdown (collapsible) -->
            <div v-if="data.analytics.platformBreakdown && Object.keys(data.analytics.platformBreakdown).length > 0" class="mt-2">
              <b-button 
                variant="link" 
                size="sm" 
                @click="togglePlatformBreakdown"
                class="p-0 text-muted"
              >
                <i :class="showPlatformBreakdown ? 'simple-icon-arrow-up' : 'simple-icon-arrow-down'"></i>
                Platform Details
              </b-button>
              
              <div v-if="showPlatformBreakdown" class="platform-breakdown mt-2">
                <div v-for="(metrics, platform) in data.analytics.platformBreakdown" :key="platform" class="platform-metrics mb-2">
                  <small class="text-muted font-weight-bold">{{ platform.toUpperCase() }}</small>
                  <div class="d-flex flex-wrap gap-1 mt-1">
                    <span v-if="metrics.likes > 0" class="small-badge likes">
                      <i class="simple-icon-heart"></i>
                      {{ formatNumber(metrics.likes) }}
                    </span>
                    <span v-if="metrics.comments > 0" class="small-badge comments">
                      <i class="simple-icon-bubble"></i>
                      {{ formatNumber(metrics.comments) }}
                    </span>
                    <span v-if="metrics.shares > 0" class="small-badge shares">
                      <i class="simple-icon-share"></i>
                      {{ formatNumber(metrics.shares) }}
                    </span>
                    <span v-if="metrics.views > 0" class="small-badge views">
                      <i class="simple-icon-eye"></i>
                      {{ formatNumber(metrics.views) }}
                    </span>
                    <span v-if="metrics.reach > 0" class="small-badge reach">
                      <i class="simple-icon-people"></i>
                      {{ formatNumber(metrics.reach) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-row>

      </div>


       <!-- Button Block  for reward-->
      <ButtonBlockInListReward
        v-if="isRewardItem"
        :config="{
          id:data.id,
          name:`${cyPrefix}`,
          points:{
            requiredPoints:data.requiredPoints,
          },
          toggleBtn:{
            status: data.status,
            disabled: !data.isValid,
            change: function(payload){ updateStatus(payload); }
          },
          imgBtnDuplicate: {
            click: function(){ onDuplicateItem(); }
          },
          imgBtnEdit: {
            click: function(){ onEditItem()}
          },
          imgBtnDelete: {
            click: function(){ openDialogAlert(); }
          },
          expireDate: data.availableTo
        }"
      />
    </div>
  </b-card>
  </b-colxx>
</template>

<script>
import DialogAlert              from '@/components/Common/DialogAlert'
import DialogDateTime           from '@/components/Common/DialogDateTime'
import BadgeListPlatform        from '@/components/Badges/BadgeListPlatform'
import BadgeListCategory        from '@/components/Badges/BadgeListCategory'
import BadgeListAddonGroup      from '@/components/Badges/BadgeListAddonGroup'
import BadgeListProductGroup    from '@/components/Badges/BadgeListProductGroup'
import ButtonBlockInListProduct from '@/components/Forms/ButtonBlockInListProduct'
import ButtonBlockInListReward  from '@/components/Forms/ButtonBlockInListReward'

// utils
import { formatNumber } from "@/utils/general.js"
 

// Notifications
import {
  addNotifyWarning,
} from "@/utils/notify.js"

export default {
  props:{
    // {string} Prefix for data-cy.
    cyPrefix: {
      type: String,
      default: ""
    },
    // {Object} Data object: Refer to PRODUCT_MODEL/ADDON_MODEL/PRODUCT_GROUP_MODEL/ADDON_GROUP_MODEL/MODEL_PROMOTION_READ.
    data: {
      type: Object,
      default:() => {}
    },
    // {string[]} Selected items' Id to check if the current list is selected or not.
    selectedItems:{
      type: Array,
      default:() => []
    },
    // {string[]} List of buttons in the components.
    buttons:{
      type: Array,
      default:() => []
    },
    // {String} Display reason why the item is disabled. Ex: Add-on not belong to group
    disabledReason: {
      type: String,
      default: ''
    },
    // {Boolean} Whether the elements of the list are shaking to drag and drop
    isDraggable: {
      type: Boolean,
      default: false
    },
    highlight: {
      type: Boolean,
      default: false
    }
  },
  components: {
    BadgeListPlatform,
    DialogAlert,
    DialogDateTime,
    BadgeListCategory,
    BadgeListAddonGroup,
    BadgeListProductGroup,
    ButtonBlockInListProduct,
    ButtonBlockInListReward,
  },
   data(){
    return {
      dialogs: {          // Dialog controls
        schedule:false,
        alert:false,
      },
      showPlatformBreakdown: false
    }
  },
  computed: {
    /**
     * To check if it is ProductGroup/AddonGroup.
     */
    isGroup: function () {
      return Array.isArray(this.data.products);
    },
    /**
     * To check if the ProductGroup/AddonGroup has Products/Addons.
     */
    isProductsInGroup : function() {
      return (Array.isArray(this.data.products) && (this.data.products.length > 0));
    },
    /**
     * To check if an Add-on belong to any Add-on group
     */
    isOrphanedAddon: function(){
      return this.data.isAddon && this.data.groups.length == 0;
    },
    /**
     * To check if it is reward item.
     */
    isRewardItem : function() {
      return this.data.requiredPoints ? true : false;
    },
    formattedDate : function() {
      return new Date(this.data.scheduleDate).toLocaleString();
    },
    platformBadges() {
      // If already have postIds (posted posts), show those
      if (Array.isArray(this.data.postIds) && this.data.postIds.length > 0) {
        return this.data.postIds
      }
      // Pending/scheduled: try to infer platforms from accounts/metadata
      const badges = []
      if (Array.isArray(this.data.platforms) && this.data.platforms.length > 0) {
        this.data.platforms.forEach(p => {
          badges.push({ platform: String(p), postUrl: null })
        })
      } else if (Array.isArray(this.data.accounts) && this.data.accounts.length > 0) {
        this.data.accounts.forEach(p => {
          badges.push({ platform: String(p), postUrl: null })
        })
      } else if (this.data.platform && typeof this.data.platform === 'string') {
        badges.push({ platform: this.data.platform, postUrl: null })
      }
      return badges
    },
    
  },
  methods:{
    
    // Notifications
    addNotifyWarning,

    /**
     * On Click Event handler.
     *
     * It handles on click event.
     * Especially, it prevents being selected with image buttons and switch toggles in the list.
     *
     * @param {Object} event Event object
     * @param {string} itemId Item's Id
     */
    onClickEvent(event, itemId) {
      // console.log(event);
      // NOTE : Whenever the <toggle-button> is clicked, two events are called.
      //        Therefore, these condition needs to be applied.
      if((event.target.classList[0] != "v-switch-core")       // To avoid switch click as select toggle
        && (event.target.classList[0] != "v-switch-label")    // To avoid switch click as select toggle
        && (event.target.classList[0] != "v-switch-button")   // To avoid switch click as select toggle
        && (event.target.classList[0] != "no-checkbox")       // To avoid click as toggle
        && (event.target.classList[0] != "badge")             // To avoid badge click as select toggle
        && (event.target.localName != "a")                    // To avoid x in a badge click as select toggle
        ) {
          if ((event.target.labels === undefined) // For all others
          // || ((event.target.type == "checkbox") && (event.target.id == "item_checkbox")) // For checkbox
          ) {
            // console.log("toggle", this.isSelected);
            this.$emit('toggle-item', event, itemId);
          }
      } else {}
    },



    /**
     * Delete Item - Product/Addon/ProductGroup/AddonGroup.
     */
    deleteItem() {
      this.data.showOnly = false;
      this.$emit('delete-item', this.data.id);
    },

    /**
     * Update schedule.
     * @param {Object} data Schedule data object: Refer EMPTY_SCHEDULE.
     */
    updateSchedule(data) {
      // console.log(data);
      this.data.hideUntil = data.hideUntil;
      this.data.availableTime = data.availableTime;
      this.data.isSyncedInGroup = data.isSyncedInGroup;
      // console.log(this.data);
      this.$emit('update-schedule', this.data);
    },

     /**
     * Update status of the item
     * @param {string} status Status: "ACTIVE" or "INACTIVE"
     */
    updateStatus(status) {
      // ProudctGroup/AddonGroup
      if(this.isGroup){
        if (this.isProductsInGroup) {
          this.data.status = status;
          this.$emit('update-status',this.data);
        }
      }
      // Product/Addon
      else {
        this.data.status = status;
        this.$emit('update-status', this.data);
      }
    },

    /**
     * On click title.
     */
    onClickTitle() {
      if(this.cyPrefix.includes('reward')) {
        this.$emit('show-item');  // For Reward item show dialog
      } else {
        this.onShowItem();        // For Product show dialog
      }
    },

    /**
     * On click duplicate button.
     */
    onDuplicateItem() {
      // console.log('onDuplicateItem', this.data);
      if (this.isRewardItem){
        this.$emit('duplicate-item', this.data);
      } else {
        this.data.showOnly = false;
        let baseData = JSON.parse(JSON.stringify(this.data)); // Copy
        delete baseData.id;       // Id is removed for Add mode
        delete baseData.images;   // Images is removed since it needs to be selected again.
        baseData.name = baseData.name + "_copy"; // Edit the name to +_copy to indicate it's duplicated item.
        this.$emit('duplicate-item', baseData);
      }
    },
    /**
     * On click show button.
     */
    onShowItem(){
      // console.log("onShowItem", this.data);
      this.data.showOnly = true;
      this.$emit('edit-item');
      this.data.showOnly = false;
    },
    /**
     * On click add button.
     */
    onAddItem() {
      this.$emit('add-item', this.data);
    },
     /**
     * On click edit button.
     */
    onEditItem() {
      // console.log("onEditItem", this.data);
      this.data.showOnly = false;
      this.$emit('edit-item',this.data);
    },


    // Dialog operations - Schedule
    /**
     * Close Schedule dialog.
     */
    closeDialogSchedule() {
      this.dialogs.schedule = false;
    },
    /**
     * Open Schedule dialog.
     */
    openDialogSchedule() {
      this.dialogs.schedule = true;
    },
    /**
     * Submit from schedule dialog.
     * @param {Object} data Schedule data object: Refer EMPTY_SCHEDULE
     */
    onSubmitDialogSchedule(data) {
      this.updateSchedule(data);
    },

    // Dialog operations - Delete Alert
    /**
     * Close Alert dialog - delete
     */
    closeDialogAlert() {
      this.dialogs.alert = false;
    },
    /**
     * On Open alert dialog - delete
     */
    openDialogAlert() {
      this.dialogs.alert = true;
    },
    /**
     * On Submit alert dialog - delete
     */
    onSubmitDialogAlert() {
      this.deleteItem();
    },
    /**
     * Format number
     */
    formatNumber(num){
      const isToFixed = true;
      return formatNumber(num, isToFixed);
    },
    togglePlatformBreakdown() {
      this.showPlatformBreakdown = !this.showPlatformBreakdown;
    },
    /**
     * Format date string to locale date string
     */
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const d = new Date(dateString);
        return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
      } catch(e) {
        return new Date(dateString).toLocaleString();
      }
    },
    /**
     * Format status string with first letter capitalized
     */
    formatStatus(status) {
      if (!status) return '';
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  },
  mounted(){
  },
  created(){
    // console.log(this.data);
  },
}
</script>

<style src="@/assets/css/sass/style_custom.scss" lang="scss" scoped></style>

<style lang="scss" scoped>
.engagement-metrics {
  .engagement-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: white;
    
    i {
      font-size: 10px;
    }
    
    &.likes {
      background-color: #e91e63;
    }
    
    &.comments {
      background-color: #2196f3;
    }
    
    &.shares {
      background-color: #4caf50;
    }
    
    &.views {
      background-color: #ff9800;
    }
    
    &.reach {
      background-color: #9c27b0;
    }
  }
  
  .platform-breakdown {
    border-top: 1px solid #e9ecef;
    padding-top: 8px;
    
    .platform-metrics {
      .small-badge {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        padding: 2px 6px;
        border-radius: 8px;
        font-size: 10px;
        font-weight: 500;
        color: white;
        
        i {
          font-size: 8px;
        }
        
        &.likes {
          background-color: #e91e63;
        }
        
        &.comments {
          background-color: #2196f3;
        }
        
        &.shares {
          background-color: #4caf50;
        }
        
        &.views {
          background-color: #ff9800;
        }
        
        &.reach {
          background-color: #9c27b0;
        }
      }
    }
  }
}

.gap-1 { gap: 0.25rem !important; }
.gap-2 { gap: 0.5rem !important; }

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  margin-left: 4px;
  text-transform: capitalize;
}
.status-badge.success { background: #4caf50; }
.status-badge.pending { background: #ff9800; }
.status-badge.error, .status-badge.failed { background: #e91e63; }

.highlight-latest {
  border: 3px solid #fbc02d !important;
  box-shadow: 0 0 12px 2px #ffe082;
  position: relative;
}
.highlight-latest::after {
  content: 'NEW';
  position: absolute;
  top: 10px;
  right: 20px;
  background: #fbc02d;
  color: #333;
  font-weight: bold;
  padding: 2px 10px;
  border-radius: 8px;
  font-size: 0.9rem;
  z-index: 2;
}
</style>
